import { useMemo, useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';
import Typography from '@mui/material/Typography';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { CopyTemplateDialog } from './CopyTemplateDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { selectedTemplateId, selectedLogsTemplateType } from '../state';
import { HOST_TEMPLATE_DISPLAY } from '../constants';
import {
  AllLogsTemplatesByTypeDocument,
  GetVersionsDocument,
  useAddLogsTemplateMutation,
  useAllLogsTemplatesByTypeQuery,
  useAllStructuresQuery,
  useCreateLogsTemplateMutation,
  useCopyLogsTemplateMutation,
  useSyncLogsTemplatesMutation,
  LogsTemplateDocument,
  LogCategory,
  LogTemplateType,
} from '@/__generated__/graphql';
import { removeTypename, useSnackBarMessage } from '../utils';

type SharedLogsTemplateActionsProps = {
  structureId: string;
  currentStructureName: string;
  linkedStructureIds: string[];
};

export const SharedLogsTemplateActions = ({
  structureId,
  currentStructureName,
  linkedStructureIds,
}: SharedLogsTemplateActionsProps) => {
  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState<boolean>(false);
  const [isSyncConfirmOpen, setIsSyncConfirmOpen] = useState(false);
  const selectedTemplateType = useAtomValue(selectedLogsTemplateType);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const { data: allStructuresData } = useAllStructuresQuery({
    variables: {
      input: {
        isActive: true,
      },
    },
  });

  const { data: allLogsTemplates, loading: isListLoading } = useAllLogsTemplatesByTypeQuery({
    variables: {
      input: {
        structureId,
        type: selectedTemplateType,
      },
    },
    skip: !structureId || !selectedAssemblyTemplateId,
    fetchPolicy: 'cache-first',
  });

  const linkedStructureNames = useMemo(() => {
    const allStructures = allStructuresData?.allStructures ?? [];
    if (linkedStructureIds.length === 0 || allStructures.length === 0) {
      return [];
    }

    const linkedStructureIdSet = new Set(linkedStructureIds);
    return allStructures
      .filter((structure) => linkedStructureIdSet.has(structure.id))
      .map((structure) => structure.name);
  }, [allStructuresData?.allStructures, linkedStructureIds]);

  const logsTemplate = useMemo(() => {
    return removeTypename(allLogsTemplates?.allLogsTemplates?.at(0));
  }, [allLogsTemplates?.allLogsTemplates]);

  const downloadJson = useCallback(() => {
    const jsonData = JSON.stringify(logsTemplate, undefined, 2);
    const jsonBlob = new Blob([jsonData], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(jsonBlob);
    const label = HOST_TEMPLATE_DISPLAY[selectedTemplateType] ?? 'log';
    const displayName = logsTemplate?.name ?? `${label} template`;
    const fileName = `${displayName}-${Date.now().toString()}.json`;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
  }, [logsTemplate, selectedTemplateType]);

  const [createNewTemplate] = useCreateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [addWorkpackTemplate] = useAddLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [copyWorkpackTemplate] = useCopyLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [syncWorkpackTemplates, { loading: isSyncing }] = useSyncLogsTemplatesMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const addNewTemplate = useCallback(() => {
    createNewTemplate({
      variables: {
        input: {
          structureId,
          type: selectedTemplateType,
        },
      },
      onError: (errorResponse: { message: string }) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsOpen(false);
      },
    }).then((response) => {
      const category =
        selectedTemplateType === LogTemplateType.CostLogTemplate
          ? LogCategory.CostLog
          : LogCategory.Worklog;

      addWorkpackTemplate({
        variables: {
          input: {
            logsTemplateId: response?.data?.createLogsTemplate?.id ?? '',
            structureId,
            category,
          },
        },
        onCompleted: (response: { addLogsTemplate?: unknown }) => {
          if (response?.addLogsTemplate) {
            const label = HOST_TEMPLATE_DISPLAY[selectedTemplateType] ?? 'Logs';
            showSnackBar(`${label} template created successfully`, 'success');
            setIsOpen(false);
          }
        },
        onError: (errorResponse: { message: string }) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsOpen(false);
        },
      });
    });
  }, [createNewTemplate, addWorkpackTemplate, structureId, showSnackBar, selectedTemplateType]);

  const copyTemplate = useCallback(() => {
    setIsCopyDialogOpen(true);
  }, []);

  const handleCopyTemplate = useCallback(
    (sourceStructureId: string, sourceTemplateId: string) => {
      copyWorkpackTemplate({
        variables: {
          input: {
            sourceStructureId,
            sourceTemplateId,
            targetStructureId: structureId,
          },
        },
        onCompleted: (response: { copyLogsTemplate?: unknown }) => {
          if (response?.copyLogsTemplate) {
            const label = HOST_TEMPLATE_DISPLAY[selectedTemplateType] ?? 'Logs';
            showSnackBar(`${label} template copied successfully`, 'success');
            setIsCopyDialogOpen(false);
          }
        },
        onError: (errorResponse: { message: string }) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsCopyDialogOpen(false);
        },
      });
    },
    [copyWorkpackTemplate, structureId, selectedTemplateType, showSnackBar]
  );

  const handleSyncTemplates = useCallback(() => {
    if (!logsTemplate?.id) {
      showSnackBar('No template selected for sync', 'error');
      return;
    }

    setIsSyncConfirmOpen(false);
    syncWorkpackTemplates({
      variables: {
        input: {
          structureId,
          templateId: logsTemplate.id,
        },
      },
      onCompleted: (response: { syncLogsTemplates?: unknown }) => {
        if (response?.syncLogsTemplates) {
          const label = HOST_TEMPLATE_DISPLAY[selectedTemplateType] ?? 'Logs';
          showSnackBar(`${label} templates synced successfully`, 'success');
        }
      },
      onError: (errorResponse: { message: string }) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
      },
    });
  }, [logsTemplate?.id, syncWorkpackTemplates, structureId, showSnackBar, selectedTemplateType]);

  return (
    <>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
          {logsTemplate && !isListLoading ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Tooltip title="This will sync linked structures" arrow placement="top">
                <Button
                  startIcon={
                    isSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                  }
                  variant="contained"
                  onClick={() => setIsSyncConfirmOpen(true)}
                  sx={containedButtonStyle}
                  disabled={isSyncing || linkedStructureIds.length === 0}
                >
                  Sync Templates
                </Button>
              </Tooltip>
              <Button
                startIcon={<DownloadIcon />}
                variant="contained"
                onClick={() => downloadJson()}
                sx={containedButtonStyle}
              >
                Download JSON
              </Button>
            </Stack>
          ) : (
            <>
              <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={() => setIsOpen(true)}
              >
                Add New Template
              </Button>
              <Button
                startIcon={<ContentCopyIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={copyTemplate}
              >
                Copy Template
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {isOpen && (
        <GenericDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title="Create new template"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to create a new template?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsOpen(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={addNewTemplate} sx={containedButtonStyle}>
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      <GenericDialog
        open={isSyncConfirmOpen}
        onClose={() => setIsSyncConfirmOpen(false)}
        title="Sync Templates"
        content={
          <Box>
            <Typography fontWeight={400} fontSize="1.4rem" color={abyssColors.primary[500]}>
              The following structures will be synced with {currentStructureName}.
            </Typography>
            <Box component="ul" sx={{ my: 2, pl: 3 }}>
              {linkedStructureNames.map((structureName) => (
                <Typography
                  key={structureName}
                  component="li"
                  fontWeight={400}
                  fontSize="1.4rem"
                  color={abyssColors.primary[500]}
                >
                  {structureName}
                </Typography>
              ))}
            </Box>
            <Typography fontWeight={400} fontSize="1.4rem" color={abyssColors.primary[500]}>
              Are you sure you want to continue?
            </Typography>
          </Box>
        }
        actions={
          <>
            <Button variant="text" onClick={() => setIsSyncConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSyncTemplates}
              disabled={isSyncing}
              sx={containedButtonStyle}
            >
              Confirm
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
      <CopyTemplateDialog
        open={isCopyDialogOpen}
        onClose={() => setIsCopyDialogOpen(false)}
        onCopy={handleCopyTemplate}
        currentStructureId={structureId}
      />
    </>
  );
};
